import { useCallback, useEffect, useMemo, useState } from 'react';
import { AssignmentType } from '../../../dtos/generated/AssignmentType';
import { BrokerDto, DriverDto, EquipmentDto, OrderByType, OrderLineItemDto, RegionDto } from '../../../dtos';
import {
    IFormFieldValidationConfig,
    IFormProps,
    isNotBlank,
    isShorterThanMaxLength,
    runFieldValidation,
    runFormValidation,
} from '../../../Components/CoreLib/library';
import _ from 'lodash';
import { EventModel } from '@bryntum/scheduler';
import { areDateTimesEqual, countries, LineItemTypes, toEasternWallDate } from '../../../util';
import { fromSchedulerDate } from '../DispatchesHelper';
import { usePatchOrderLineItemUpdateDispatchBillingMemoMutation } from '../../../store/generated/generatedApi';
import { Address } from '../../../dtos/Address';

export interface IUseEventEditorFormProps extends IFormProps<EventModel> {
    isHaulerView: boolean;
    calculateRemainingHaulQuantity: (resourceId: string, excludeDispatchId?: string) => number | undefined;
    orderLineItem?: OrderLineItemDto;
}

export interface IEventEditorFormValues {
    startTime?: Date | null;
    endTime?: Date | null;
    onsiteTime?: Date | null;
    yardTime?: Date | null;
    assignmentType?: AssignmentType;
    driver?: DriverDto;
    driverId?: string;
    broker?: BrokerDto;
    brokerId?: string;
    equipment?: EquipmentDto;
    equipmentId?: string;
    haulToAddressLine1?: string;
    haulToAddressLine2?: string;
    haulToCity?: string;
    haulToState?: string;
    haulToZipCode?: string;
    haulToCountry?: string;
    haulToDirections?: string;
    haulFromAddressLine1?: string;
    haulFromAddressLine2?: string;
    haulFromCity?: string;
    haulFromState?: string;
    haulFromZipCode?: string;
    haulFromCountry?: string;
    haulFromDirections?: string;
    haulFromSiteName?: string;
    haulToSiteName?: string;
    haulToRegion?: RegionDto;
    haulFromRegion?: RegionDto;
    haulToRegionId?: string;
    haulFromRegionId?: string;
    driverComment?: string;
    haulQuantity?: number;
}

export function useEventEditorForm(props: IUseEventEditorFormProps) {
    const { save, cancel, initValues, isHaulerView, orderLineItem } = props;
    const [startTime, setStartTime] = useState<Date | undefined | null>(initValues ? fromSchedulerDate((initValues as any).data.startDate) as Date : null);
    const [endTime, setEndTime] = useState<Date | undefined | null>(initValues ? fromSchedulerDate((initValues as any).data.endDate) as Date : null);
    const [onsiteTime, setOnsiteTime] = useState<Date | undefined | null>(initValues ? new Date((initValues as any).data.onsiteTime) : null);
    const [yardTime, setYardTime] = useState<Date | undefined | null>(
        initValues && (initValues as any).data.yardTime ? new Date((initValues as any).data.yardTime) : null
    );
    const [assignmentType, setAssignmentType] = useState<AssignmentType>();
    const [driver, setDriver] = useState<DriverDto>();
    const [broker, setBroker] = useState<BrokerDto>();
    const [equipment, setEquipment] = useState<EquipmentDto>();
    const [orderLineItemId, setOrderLineItemId] = useState<string | undefined>();
    const [formHaulToAddressLine1, setFormHaulToAddressLine1] = useState('');
    const [formHaulToAddressLine2, setFormHaulToAddressLine2] = useState('');
    const [formHaulToCity, setFormHaulToCity] = useState('');
    const [formHaulToState, setFormHaulToState] = useState('IN');
    const [formHaulToZipCode, setFormHaulToZipCode] = useState('');
    const [formHaulToCountry, setFormHaulToCountry] = useState(countries[2]);
    const [formHaulToDirections, setFormHaulToDirections] = useState('');
    const [formHaulFromAddressLine1, setFormHaulFromAddressLine1] = useState('');
    const [formHaulFromAddressLine2, setFormHaulFromAddressLine2] = useState('');
    const [formHaulFromCity, setFormHaulFromCity] = useState('');
    const [formHaulFromState, setFormHaulFromState] = useState('IN');
    const [formHaulFromZipCode, setFormHaulFromZipCode] = useState('');
    const [formHaulFromCountry, setFormHaulFromCountry] = useState(countries[2]);
    const [formHaulFromDirections, setFormHaulFromDirections] = useState('');
    const [formDriverComment, setFormDriverComment] = useState('');
    const [formHaulToSiteName, setFormHaulToSiteName] = useState('');
    const [formHaulFromSiteName, setFormHaulFromSiteName] = useState('');
    const [formHaulToRegion, setFormHaulToRegion] = useState<RegionDto>();
    const [formHaulFromRegion, setFormHaulFromRegion] = useState<RegionDto>();
    const [formDispatchBillingMemo, setFormDispatchBillingMemo] = useState('');
    const [formDispatchingBillingMemoIsChanged, setFormDispatchingBillingMemoIsChanged] = useState(false);
    const [formHaulQuantity, setFormHaulQuantity] = useState<number | undefined>(undefined);
    const [updateOrderLineItem] = usePatchOrderLineItemUpdateDispatchBillingMemoMutation();

    useEffect(() => {
        if (initValues) {
            const event = (initValues as any).data;
            setStartTime(event.startDate ? fromSchedulerDate(event.startDate) as Date : null);
            setEndTime(event.endDate ? fromSchedulerDate(event.endDate) as Date : null);
            setOnsiteTime(event.onsiteTime ? new Date(event.onsiteTime) : null);
            setYardTime(event.yardTime !== null ? new Date(event.yardTime) : null);
            setAssignmentType(event.assignmentType);
            setDriver(event.driver);
            setBroker(event.broker);
            setEquipment(event.equipment ?? event.driver?.equipment);
            setOrderLineItemId(event.orderLineItemId);
            setFormHaulToAddressLine1(event?.haulToAddressLine1 ?? '');
            setFormHaulToAddressLine2(event?.haulToAddressLine2 ?? '');
            setFormHaulToCity(event?.haulToCity ?? '');
            setFormHaulToState(event?.haulToState ?? 'IN');
            setFormHaulToZipCode(event?.haulToZipCode ?? '');
            setFormHaulToCountry(event?.haulToCountry ?? countries[2]);
            setFormHaulToDirections(event?.haulToDirections ?? '');
            setFormHaulFromAddressLine1(event?.haulFromAddressLine1 ?? '');
            setFormHaulFromAddressLine2(event?.haulFromAddressLine2 ?? '');
            setFormHaulFromCity(event?.haulFromCity ?? '');
            setFormHaulFromState(event?.haulFromState ?? 'IN');
            setFormHaulFromZipCode(event?.haulFromZipCode ?? '');
            setFormHaulFromCountry(event?.haulFromCountry ?? countries[2]);
            setFormHaulFromDirections(event?.haulFromDirections ?? '');
            setFormDriverComment(event?.driverComment ?? '');
            setFormHaulToSiteName(event?.haulToSiteName ?? '');
            setFormHaulFromSiteName(event?.haulFromSiteName ?? '');
            setFormHaulToRegion(event?.haulToRegion);
            setFormHaulFromRegion(event?.haulFromRegion);
            setFormDispatchBillingMemo(event?.dispatchBillingMemo ?? '');
            setFormHaulQuantity(event?.haulQuantity ?? undefined);
        }
    }, [initValues]);

    const [fieldErrors, setFieldErrors] = useState<Map<keyof IEventEditorFormValues, string>>(
        new Map([
            ['startTime', ''],
            ['endTime', ''],
            ['assignmentType', ''],
            ['brokerId', ''],
            ['driverId', ''],
            ['equipmentId', ''],
            ['haulToAddressLine1', ''],
            ['haulToCity', ''],
            ['haulToState', ''],
            ['haulToZipCode', ''],
            ['haulToCountry', ''],
            ['haulToDirections', ''],
            ['haulFromAddressLine1', ''],
            ['haulFromCity', ''],
            ['haulFromState', ''],
            ['haulFromZipCode', ''],
            ['haulFromCountry', ''],
            ['haulFromDirections', ''],
            ['haulFromSiteName', ''],
            ['haulToSiteName', ''],
            ['haulToRegion', ''],
            ['haulFromRegion', ''],
            ['haulToRegionId', ''],
            ['haulFromRegionId', ''],
            ['haulQuantity', ''],
        ])
    );

    const isTimeValid = useCallback(() => {
        return (value: any) => {
            if (startTime && endTime) {
                let isValid = !(new Date(startTime) >= new Date(endTime));

                const errorMessageBuilder = (_fieldName: string) => `Start time must be before end time.`;
                return {
                    isValid,
                    errorMessageBuilder,
                };
            } else {
                return {
                    isValid: false,
                    errorMessageBuilder: (_fieldName: string) => `Start time and end time are required.`,
                };
            }
        };
    }, [endTime, startTime]);

    const isHaulQuantityValid = useCallback(() => {
        return (value: any) => {
            let maxHaulQuantity: number | undefined = undefined;
            const hasHaulQuantity = orderLineItem?.orderBy !== OrderByType.Equipment && !(orderLineItem?.lineItemType === LineItemTypes.Hourly && orderLineItem?.orderBy === OrderByType.Quantity);
            if (!hasHaulQuantity) {
                return {
                    isValid: true,
                    errorMessageBuilder: (_fieldName: string) => '',
                };
            }

            if (props.calculateRemainingHaulQuantity && orderLineItemId) {
                maxHaulQuantity = props.calculateRemainingHaulQuantity(orderLineItemId, (initValues as any)?.id);
                if (maxHaulQuantity) {
                    maxHaulQuantity = Math.round(maxHaulQuantity * 10000) / 10000;
                }
            }

            if (typeof value === 'number' && value < 0) {
                return {
                    isValid: false,
                    errorMessageBuilder: (_fieldName: string) =>
                        `Haul quantity cannot be negative.`,
                };
            }

            if (
                typeof maxHaulQuantity === 'number' &&
                value > maxHaulQuantity
            ) {
                return {
                    isValid: false,
                    errorMessageBuilder: (_fieldName: string) =>
                        `Haul quantity exceeds the max quantity required for the line item: (${maxHaulQuantity} available).`,
                };
            }
            return {
                isValid: true,
                errorMessageBuilder: (_fieldName: string) => '',
            };
        };
    }, [props, orderLineItemId, initValues, orderLineItem]);

    const formFieldValidators = useMemo(() => {
        return new Map<keyof IEventEditorFormValues, IFormFieldValidationConfig>([
            [
                'startTime',
                {
                    validators: [isNotBlank, isTimeValid()],
                    errorMessageEntityName: 'Start Time',
                },
            ],
            [
                'endTime',
                {
                    validators: [isNotBlank, isTimeValid()],
                    errorMessageEntityName: 'End Time',
                },
            ],
            [
                'assignmentType',
                {
                    validators: [isNotBlank],
                    errorMessageEntityName: 'Assignment Type',
                },
            ],
            [
                'brokerId',
                {
                    validators: assignmentType === AssignmentType.Broker ? [isNotBlank] : [],
                    errorMessageEntityName: 'Broker',
                },
            ],
            [
                'driverId',
                {
                    validators: assignmentType === AssignmentType.Driver ? [isNotBlank] : [],
                    errorMessageEntityName: 'Driver',
                },
            ],
            [
                'equipmentId',
                {
                    validators: assignmentType === AssignmentType.Driver ? [isNotBlank] : [],
                    errorMessageEntityName: 'Equipment',
                },
            ],
            [
                'haulToAddressLine1',
                {
                    validators: [isShorterThanMaxLength(100)],
                    errorMessageEntityName: 'Address Line 1',
                },
            ],
            [
                'haulToCity',
                {
                    validators: [isShorterThanMaxLength(60)],
                    errorMessageEntityName: 'City',
                },
            ],
            [
                'haulToState',
                {
                    validators: [isShorterThanMaxLength(25)],
                    errorMessageEntityName: 'State',
                },
            ],
            [
                'haulToZipCode',
                {
                    validators: [isShorterThanMaxLength(12)],
                    errorMessageEntityName: 'Zip Code',
                },
            ],
            [
                'haulToCountry',
                {
                    validators: [isShorterThanMaxLength(60)],
                    errorMessageEntityName: 'Country',
                },
            ],
            [
                'haulFromAddressLine1',
                {
                    validators: [isShorterThanMaxLength(100)],
                    errorMessageEntityName: 'Address Line 1',
                },
            ],
            [
                'haulFromCity',
                {
                    validators: [isShorterThanMaxLength(60)],
                    errorMessageEntityName: 'City',
                },
            ],
            [
                'haulFromState',
                {
                    validators: [isShorterThanMaxLength(25)],
                    errorMessageEntityName: 'State',
                },
            ],
            [
                'haulFromZipCode',
                {
                    validators: [isShorterThanMaxLength(12)],
                    errorMessageEntityName: 'Zip Code',
                },
            ],
            [
                'haulFromCountry',
                {
                    validators: [isShorterThanMaxLength(60)],
                    errorMessageEntityName: 'Country',
                },
            ],
            [
                'haulQuantity',
                {
                    validators: [isHaulQuantityValid()],
                    errorMessageEntityName: 'Haul Quantity',
                },
            ]
        ]);
    }, [assignmentType, isTimeValid, isHaulQuantityValid]);

    const handleFormDispatchBillingMemoChange = useCallback((value: string) => {
        setFormDispatchBillingMemo(value);
        setFormDispatchingBillingMemoIsChanged(true);
    }, []);

    const getCurrentFormValues = useCallback((): IEventEditorFormValues => {
        return {
            startTime,
            endTime,
            onsiteTime,
            yardTime,
            assignmentType,
            driver,
            driverId: driver?.id,
            broker,
            brokerId: broker?.id,
            equipment,
            equipmentId: equipment?.id,
            haulToAddressLine1: formHaulToAddressLine1,
            haulToAddressLine2: formHaulToAddressLine2,
            haulToCity: formHaulToCity,
            haulToState: formHaulToState,
            haulToZipCode: formHaulToZipCode,
            haulToCountry: formHaulToCountry,
            haulToDirections: formHaulToDirections,
            haulFromAddressLine1: formHaulFromAddressLine1,
            haulFromAddressLine2: formHaulFromAddressLine2,
            haulFromCity: formHaulFromCity,
            haulFromState: formHaulFromState,
            haulFromZipCode: formHaulFromZipCode,
            haulFromCountry: formHaulFromCountry,
            haulFromDirections: formHaulFromDirections,
            haulFromRegion: formHaulFromRegion,
            haulFromSiteName: formHaulFromSiteName,
            haulToSiteName: formHaulToSiteName,
            haulToRegion: formHaulToRegion,
            haulToRegionId: formHaulToRegion?.id,
            haulFromRegionId: formHaulToRegion?.id,
            driverComment: formDriverComment,
            haulQuantity: formHaulQuantity,
        };
    }, [
        assignmentType,
        broker,
        driver,
        endTime,
        equipment,
        formHaulToAddressLine1,
        formHaulToAddressLine2,
        formHaulToCity,
        formHaulToState,
        formHaulToZipCode,
        formHaulToCountry,
        formHaulToDirections,
        formHaulFromAddressLine1,
        formHaulFromAddressLine2,
        formHaulFromCity,
        formHaulFromState,
        formHaulFromZipCode,
        formHaulFromCountry,
        formHaulFromDirections,
        formHaulFromRegion,
        formHaulFromSiteName,
        formHaulToSiteName,
        formHaulToRegion,
        onsiteTime,
        startTime,
        yardTime,
        formDriverComment,
        formHaulQuantity
    ]);

    const validateForm = useCallback(() => {
        const formValues = getCurrentFormValues();
        const validationResult = runFormValidation<Partial<IEventEditorFormValues>>(formValues, formFieldValidators);
        setFieldErrors(validationResult.errorMessages);
        return validationResult.isValid;
    }, [formFieldValidators, getCurrentFormValues]);

    const validateField = useCallback(
        (fieldName: keyof IEventEditorFormValues) => {
            const validationConfig = formFieldValidators.get(fieldName);
            if (validationConfig) {
                const formValues = getCurrentFormValues();
                const fieldValue = formValues[fieldName];
                const { errorMessage } = runFieldValidation(fieldValue, validationConfig);

                if (errorMessage !== fieldErrors.get(fieldName)) {
                    const updatedFieldErrors = _.cloneDeep(fieldErrors);
                    updatedFieldErrors.set(fieldName, errorMessage);
                    setFieldErrors(updatedFieldErrors);
                }
            }
        },
        [fieldErrors, formFieldValidators, getCurrentFormValues]
    );

    const isFormDirty = useMemo(() => {
        const data = (initValues as any).data;
        var isDirty = false;
        if (startTime) {
            isDirty = isDirty || !areDateTimesEqual(startTime, fromSchedulerDate(data.startDate));
        }
        if (endTime) {
            isDirty = isDirty || !areDateTimesEqual(endTime, fromSchedulerDate(data.endDate));
        }
        if (onsiteTime) {
            isDirty = isDirty || !areDateTimesEqual(onsiteTime, data.onsiteTime);
        }
        if (yardTime) {
            isDirty = isDirty || !areDateTimesEqual(yardTime, data.yardTime);
        }
        isDirty = isDirty || assignmentType !== data.assignmentType;
        isDirty = isDirty || driver?.id !== data.driverId;
        isDirty = isDirty || broker?.id !== data.brokerId;
        isDirty = isDirty || equipment?.id !== data.equipmentId;
        isDirty = isDirty || formHaulToAddressLine1 !== data?.haulToAddressLine1;
        isDirty = isDirty || formHaulToAddressLine2 !== data?.haulToAddressLine2;
        isDirty = isDirty || formHaulToCity !== data?.haulToCity;
        isDirty = isDirty || formHaulToState !== data?.haulToState;
        isDirty = isDirty || formHaulToZipCode !== data?.haulToZipCode;
        isDirty = isDirty || formHaulToCountry !== data?.haulToCountry;
        isDirty = isDirty || formHaulToDirections !== data?.haulToDirections;
        isDirty = isDirty || formHaulToSiteName !== data?.haulToSiteName;
        isDirty = isDirty || formHaulToRegion !== data?.haulToRegion;
        isDirty = isDirty || formHaulFromAddressLine1 !== data?.haulFromAddressLine1;
        isDirty = isDirty || formHaulFromAddressLine2 !== data?.haulFromAddressLine2;
        isDirty = isDirty || formHaulFromCity !== data?.haulFromCity;
        isDirty = isDirty || formHaulFromState !== data?.haulFromState;
        isDirty = isDirty || formHaulFromZipCode !== data?.haulFromZipCode;
        isDirty = isDirty || formHaulFromCountry !== data?.haulFromCountry;
        isDirty = isDirty || formHaulFromDirections !== data?.haulFromDirections;
        isDirty = isDirty || formHaulFromSiteName !== data?.haulFromSiteName;
        isDirty = isDirty || formHaulFromRegion !== data?.haulFromRegion;
        isDirty = isDirty || formDriverComment !== data?.driverMemo;
        isDirty = isDirty || formHaulQuantity !== data?.haulQuantity;
        return isDirty;
    }, [
        assignmentType,
        broker?.id,
        driver?.id,
        endTime,
        equipment?.id,
        formHaulToAddressLine1,
        formHaulToAddressLine2,
        formHaulToCity,
        formHaulToState,
        formHaulToZipCode,
        formHaulToCountry,
        formHaulToDirections,
        formHaulFromAddressLine1,
        formHaulFromAddressLine2,
        formHaulFromCity,
        formHaulFromState,
        formHaulFromZipCode,
        formHaulFromCountry,
        formHaulFromDirections,
        formHaulFromRegion,
        formHaulFromSiteName,
        formHaulToSiteName,
        formHaulToRegion,
        initValues,
        onsiteTime,
        startTime,
        yardTime,
        formDriverComment,
        formHaulQuantity,
    ]);

    const handleHaulToAddressChange = useCallback(
        (address: Address) => {
            setFormHaulToAddressLine1(address.addressLine1);
            setFormHaulToAddressLine2(address.addressLine2 ?? '');
            setFormHaulToCity(address.city);
            setFormHaulToState(address.state);
            setFormHaulToZipCode(address.zipCode);
            setFormHaulToCountry(address.country);
            setFormHaulToDirections(address.directions ?? '');
        },
        []
    );

    const handleHaulFromAddressChange = useCallback(
        (address: Address) => {
            setFormHaulFromAddressLine1(address.addressLine1);
            setFormHaulFromAddressLine2(address.addressLine2 ?? '');
            setFormHaulFromCity(address.city);
            setFormHaulFromState(address.state);
            setFormHaulFromZipCode(address.zipCode);
            setFormHaulFromCountry(address.country);
            setFormHaulFromDirections(address.directions ?? '');
        },
        [setFormHaulFromAddressLine2]
    );

    const handleSave = useCallback(() => {
        const isFormValid = validateForm();
        if (isFormValid) {
            if (initValues) {
                let data = {
                    // The scheduler record holds Eastern wall Dates; onsite/yard are plain API instants
                    startDate: startTime ? toEasternWallDate(startTime) : null,
                    endDate: endTime ? toEasternWallDate(endTime) : null,
                    onsiteTime: onsiteTime?.toISOString() ?? null,
                    yardTime: yardTime !== null ? yardTime?.toISOString() : null,
                    assignmentType,
                    driverId: driver?.id ?? null,
                    driver: driver ?? null,
                    brokerId: broker?.id ?? null,
                    broker: broker ?? null,
                    equipmentId: equipment?.id ?? null,
                    equipment: equipment ?? null,
                    eventColor: broker ? '#285C63' : '#A3271F',
                    resourceId: initValues.getData('resourceId'),
                    haulToAddressLine1: formHaulToAddressLine1,
                    haulToAddressLine2: formHaulToAddressLine2,
                    haulToCity: formHaulToCity,
                    haulToState: formHaulToState,
                    haulToZipCode: formHaulToZipCode,
                    haulToCountry: formHaulToCountry,
                    haulToDirections: formHaulToDirections,
                    haulToSiteName: formHaulToSiteName,
                    haulToRegion: formHaulToRegion,
                    haulFromAddressLine1: formHaulFromAddressLine1,
                    haulFromAddressLine2: formHaulFromAddressLine2,
                    haulFromCity: formHaulFromCity,
                    haulFromState: formHaulFromState,
                    haulFromZipCode: formHaulFromZipCode,
                    haulFromCountry: formHaulFromCountry,
                    haulFromDirections: formHaulFromDirections,
                    haulFromSiteName: formHaulFromSiteName,
                    haulFromRegion: formHaulFromRegion,
                    haulFromRegionId: formHaulFromRegion?.id ?? null,
                    haulToRegionId: formHaulToRegion?.id ?? null,
                    driverComment: formDriverComment,
                    haulQuantity: formHaulQuantity ?? undefined,
                };

                // When on the driver/broker dispatch view, we need to manually handle the moving of the
                // dispatch between the different stores to properly trigger the onUpdate listeners.
                if (isHaulerView) {
                    if (driver) {
                        data.resourceId = driver.id;
                    } else if (broker) {
                        data.resourceId = broker.id;
                    }
                } else {
                    data.resourceId = initValues.getData('resourceId');
                }
                initValues.set(data);

                save(initValues);
            }

            if (formDispatchingBillingMemoIsChanged) {
                updateOrderLineItem({
                    id: (initValues as any).data.orderLineItemId,
                    dispatchBillingMemo: formDispatchBillingMemo,
                });
            }
        }
    }, [validateForm, initValues, formDispatchingBillingMemoIsChanged, startTime, endTime, onsiteTime, yardTime, assignmentType, driver, broker, equipment, formHaulToAddressLine1, formHaulToAddressLine2, formHaulToCity, formHaulToState, formHaulToZipCode, formHaulToCountry, formHaulToDirections, formHaulToSiteName, formHaulToRegion, formHaulFromAddressLine1, formHaulFromAddressLine2, formHaulFromCity, formHaulFromState, formHaulFromZipCode, formHaulFromCountry, formHaulFromDirections, formHaulFromSiteName, formHaulFromRegion, formDriverComment, formHaulQuantity, isHaulerView, save, updateOrderLineItem, formDispatchBillingMemo]);

    const handleCancel = useCallback(() => {
        cancel();
    }, [cancel]);

    return {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        startTime,
        endTime,
        onsiteTime,
        yardTime,
        assignmentType,
        driver,
        broker,
        equipment,
        handleHaulToAddressChange,
        handleHaulFromAddressChange,
        handleFormDispatchBillingMemoChange,
        setFormDriverComment,
        formDispatchBillingMemo,
        formDriverComment,
        formHaulToAddressLine1,
        formHaulToAddressLine2,
        formHaulToCity,
        formHaulToState,
        formHaulToZipCode,
        formHaulToCountry,
        formHaulToDirections,
        formHaulFromAddressLine1,
        formHaulFromAddressLine2,
        formHaulFromCity,
        formHaulFromState,
        formHaulFromZipCode,
        formHaulFromCountry,
        formHaulFromDirections,
        formHaulToRegion,
        formHaulFromRegion,
        formHaulToSiteName,
        formHaulFromSiteName,
        formHaulQuantity,
        setFormHaulToSiteName,
        setFormHaulFromSiteName,
        setFormHaulToRegion,
        setFormHaulFromRegion,
        setStartTime,
        setEndTime,
        setOnsiteTime,
        setYardTime,
        setAssignmentType,
        setDriver,
        setBroker,
        setEquipment,
        setFormHaulQuantity,
        validateField,
    };
}
